import { useEffect, useState } from "react";
import { Printer } from "lucide-react";
import { Input } from "@/components/ui/input";
import { adminApi, inr, errMsg } from "@/lib/api";
import { Btn, PageTitle } from "@/admin/ui";
import { SummaryPanel } from "@/admin/SummaryPanel";

const todayIst = () => new Date(Date.now() + 19800000).toISOString().slice(0, 10);
const CARDS = [["gross", "Gross sales"], ["cod", "Cash (COD)"], ["upi", "UPI"], ["bank", "Bank transfer"], ["refunds", "Refunds"], ["net", "Net sales"]];
const COLS = [["orders", "Orders"], ["delivered", "Delivered"], ["cancelled", "Cancelled"], ["gross", "Gross", 1], ["cod", "Cash", 1], ["upi", "UPI", 1], ["bank", "Bank", 1], ["refunds", "Refunds", 1], ["net", "Net", 1]];

const Row = ({ r, label, bold }) => (
  <tr className={`border-b border-stone-200 ${bold ? "font-extrabold bg-stone-50" : ""}`} data-testid={`sales-row-${r.date || "total"}`}>
    <td className="py-2 pr-3 whitespace-nowrap">{label}</td>
    {COLS.map(([k, , money]) => <td key={k} className="text-right px-2 whitespace-nowrap">{money ? inr(r[k]) : r[k]}</td>)}
  </tr>
);

export default function AdminSalesReport() {
  const [start, setStart] = useState(todayIst());
  const [end, setEnd] = useState(todayIst());
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    setErr("");
    adminApi.get("/sales-report", { params: { start, end } }).then((r) => setData(r.data)).catch((e) => { setData(null); setErr(errMsg(e)); });
  }, [start, end]);

  return (
    <div className="max-w-5xl">
      <PageTitle sub="By delivery date · every order counted whatever its payment status; cancelled excluded from money totals" action={
        <div className="flex flex-wrap items-center gap-2 print:hidden">
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-9 w-40 bg-white" data-testid="sales-start-input" />
          <span className="text-sm text-stone-500">to</span>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9 w-40 bg-white" data-testid="sales-end-input" />
          <Btn onClick={() => window.print()} disabled={!data} data-testid="sales-print-btn"><Printer className="w-4 h-4 inline mr-1" />Print</Btn>
        </div>}>Daily sales report</PageTitle>
      {err && <p className="text-sm text-accent mb-3" data-testid="sales-error">{err}</p>}
      {data && (
        <>
          <p className="hidden print:block text-sm font-bold mb-2">Mukti Fresh Vegetable · {data.start}{data.end !== data.start ? ` to ${data.end}` : ""}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
            {CARDS.map(([k, label]) => (
              <div key={k} className={`rounded-md border p-3 ${k === "net" ? "bg-primary text-white border-primary" : "bg-white border-stone-200"}`} data-testid={`sales-card-${k}`}>
                <p className="text-[10px] uppercase tracking-wider font-bold opacity-70">{label}</p>
                <p className="text-xl font-extrabold mt-1">{inr(data.total[k])}</p>
              </div>
            ))}
          </div>
          <p className="text-sm text-stone-600 mb-2" data-testid="sales-counts">{data.total.orders} orders · {data.total.delivered} delivered · {data.total.cancelled} cancelled</p>
          <div className="bg-white rounded-md border border-stone-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[10px] uppercase tracking-wider border-b border-stone-300"><th className="p-2">Date</th>{COLS.map(([k, l]) => <th key={k} className="text-right px-2">{l}</th>)}</tr></thead>
              <tbody>
                {data.days.map((r) => <Row key={r.date} r={r} label={r.date} />)}
                {data.days.length === 0 && <tr><td colSpan={10} className="p-4 text-center text-stone-500" data-testid="sales-empty">No orders in this period.</td></tr>}
                {data.days.length > 1 && <Row r={data.total} label="Total" bold />}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-stone-500 mt-2">Cash, UPI and bank columns are order amounts after removed-item refunds. Cancelled orders are counted but excluded from money totals.</p>
        </>
      )}
      <SummaryPanel date={end} />
    </div>
  );
}
